import React, { useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import get from 'lodash/get';
import ToggleSwitch from '../../../../common/ToggleSwitch.jsx';
import Select from '../../../../common/Select.jsx';
import commonStyles from '../../../gen2_widgets/base/CommonSettings.module.scss';
import styles from './FormatterOptions.module.scss';
import TextInput from '../../../../common/TextInput.jsx';
import { useDispatch } from 'react-redux';
import { closeModal, openModal } from '../../../redux/dashboardSlice.js';
import CommonRow from './CommonRow.jsx';
import Button from '../../../../bootstrap/Button.jsx';
import Portal from '../../../../common/Portal.jsx';
import Dialog, { DialogFooter, DialogHeader } from '../../../../bootstrap/Dialog.jsx';

export const NUMBER_FORMAT_OPTIONS = [
  { value: '0.0f', label: '1 decimal (12.3)' },
  { value: '0.2f', label: '2 decimals (12.34)' },
  { value: '0.0%', label: I18n.t('formatter_preset_percent') },
  { value: '0.2%', label: I18n.t('formatter_preset_percent_decimals') },
  { value: '$0,.2f', label: I18n.t('formatter_preset_currency') },
  { value: ',.0f', label: I18n.t('formatter_preset_thousands') },
  { value: '+.0f', label: I18n.t('formatter_preset_signed') },
  { value: '0>6.2f', label: I18n.t('formatter_preset_zero_padded') },
  { value: '.2s', label: I18n.t('formatter_preset_si_units') },
];

export const DATE_FORMAT_OPTIONS = [
  { value: '%Y', label: I18n.t('formatter_preset_year') },
  { value: '%b %Y', label: I18n.t('formatter_preset_month_year') },
  { value: '%d %b', label: I18n.t('formatter_preset_day_month') },
  { value: '%d/%m/%Y', label: I18n.t('formatter_preset_dmy') },
  { value: '%m/%d/%Y', label: I18n.t('formatter_preset_mdy') },
  { value: '%H:%M', label: I18n.t('formatter_preset_time') },
  { value: '%b %d, %H:%M', label: I18n.t('formatter_preset_date_time') },
];

const DEFAULT_OPTIONS = [...NUMBER_FORMAT_OPTIONS, ...DATE_FORMAT_OPTIONS];

function CustomFormatterDialog(props) {
  const { fullAttr, selectedObject, setAttribute, defaultValue, disabled, isClearable = true } = props;

  return (
    <Dialog {...props}>
      <Scrollbars style={{height: 'calc(100vh - 240px)', overflowX: 'hidden'}}>
        <TextInput value={get(selectedObject, fullAttr)} afterChange={v => setAttribute(fullAttr, v)} disabled={disabled} />

        <div>
          <h2>{I18n.t('custom_format')}</h2>

          <section>
            <h3>{I18n.t('time_format')}</h3>
            <p>{I18n.t('date_time_config_info')} <span className='text-danger' dangerouslySetInnerHTML={{__html: I18n.t('time_format_axes_config_warning')}}></span></p>
            <ul>
              <li><code>%Y</code> 2025</li>
              <li><code>%y</code> 25</li>
              <li><code>%m</code> 01-12</li>
              <li><code>%b</code> Jan</li>
              <li><code>%B</code> January</li>
              <li><code>%d</code> 01-31</li>
              <li><code>%H</code> 00-23</li>
              <li><code>%I</code> 01-12</li>
              <li><code>%M</code> 00-59</li>
              <li><code>%S</code> 00-59</li>
              <li><code>%p</code> AM/PM</li>
            </ul>
            <p>{I18n.t('examples')}:</p>
            <ul>
              <li><code>%Y-%m-%d</code> → 2025-09-08</li>
              <li><code>%d.%m.%Y</code> → 08.09.2025</li>
              <li><code>%H:%M</code> → 14:05</li>
              <li><code>%I:%M %p</code> → 02:05 PM</li>
              <li><code>%b %d, %Y</code> → Sep 08, 2025</li>
            </ul>
            <p>{I18n.t('text_tokens_mixing_info')}: <code>%d %b %Y, %H:%M</code></p>
          </section>

          <section>
            <h3>{I18n.t('number_format')}</h3>
            <p>{I18n.t('pattern')}: <code>[[fill]align][sign][#][$][0][width][,][.precision][~][type]</code></p>
            <ul>
              <li><code>,</code> {I18n.t('thousands')}</li>
              <li><code>.2f</code> 2 {I18n.t('decimals')}</li>
              <li><code>%</code> {I18n.t('percent')}</li>
              <li><code>s</code> {I18n.t('compact')} (k, M)</li>
              <li><code>$</code> {I18n.t('currency_symbol')}</li>
              <li><code>~</code> {I18n.t('trim_trailing_zeros')}</li>
            </ul>
            <p>{I18n.t('examples')}:</p>
            <ul>
              <li><code>,.2f</code> → 1,234.60</li>
              <li><code>,d</code> → 1,234</li>
              <li><code>.0%</code> → 123400%</li>
              <li><code>.2s</code> → 1.23k</li>
              <li><code>$,.2f</code> → $1,234.60</li>
              <li><code>,.2~f</code> → 1,234.6</li>
            </ul>
            <p>{I18n.t('formatter_mix_text')} <code>{'USD #{,.2f}'}</code></p>
          </section>
        </div>
      </Scrollbars>
    </Dialog>
  )
}

function FormatterOptions(props) {
  const dispatch = useDispatch();
  const { attr, label, prefix, selectedObject, setAttribute, defaultValue, disabled, isClearable = true, options = DEFAULT_OPTIONS, ...rest } = props;
  const fullAttr = [prefix, attr].filter(Boolean).join('.');
  const value = get(selectedObject, fullAttr);
  const [isCustom, setIsCustom] = useState(value != null && value !== '' && !options.some(option => option.value === value));
  const [modalOpen, setModalOpen] = useState(false);

  const toggleModal = () => {
    if (modalOpen) {
      dispatch(closeModal());
    } else {
      dispatch(openModal());
    }
    setModalOpen(!modalOpen);
  }

  return (
    <div {...rest} className={commonStyles['settings-option']}>
      <label style={{display: 'flex', justifyContent: 'space-between'}}>
        <span>{label}</span>
        <span style={{marginRight: 30, position: 'relative'}}>
          <span style={{marginRight: 5}}>{I18n.t('custom')}</span>
          <ToggleSwitch
            name={`formatter-${fullAttr}`}
            isOn={isCustom}
            handleToggle={() => setIsCustom(prev => !prev)}
            size='sm'
            style={{position: 'absolute', top: 2}}
          />
        </span>
      </label>
      {isCustom
        ? <CommonRow className={styles['format-custom-input']} onClick={toggleModal}>
            <TextInput value={get(selectedObject, fullAttr)}  />
            <Button type='default'>
              {I18n.t('edit_format')}
            </Button>
          </CommonRow>
        : <Select defaultValue={defaultValue} options={options} value={get(selectedObject, fullAttr)} onChange={v => setAttribute(fullAttr, v)} disabled={disabled} isClearable={isClearable} />
      }
      {modalOpen && <Portal
        component={CustomFormatterDialog}
        /* Dialog props */
        header={<DialogHeader showBorder={true} title={I18n.t('edit_format')} onClose={toggleModal} />}
        footer={<DialogFooter addText={I18n.t('ok')} onAdd={toggleModal} onClose={toggleModal} />}
        style={modalOpen ? {display:'block'} : {display:'none'}}
        size='md'
        closeOnOutsideClick={true}
        onClose={toggleModal}
        /* CustomFormatterDialog props */
        {...props}
        fullAttr={fullAttr}
      />}
    </div>
  )
}

export default FormatterOptions;